import { useCallback, useEffect, useRef, useState } from 'react';

import { urlParser } from '@hh.ru/browser-api-utils';

import { Step, StepName } from 'src/components/VacancyCreate/contexts/StepsContext/types';
import { useFeatureEnabled } from 'src/hooks/useFeatureEnabled';
import { useIsBackoffice } from 'src/hooks/useIsBackoffice';
import { useSelector } from 'src/hooks/useSelector';

import { CombinerStep } from 'src/components/VacancyCreate/contexts/StepsContext/hooks/useStepsCombiner';

const availableStepNames = Object.values(StepName);

const isStepName = (stepName?: string | null): stepName is StepName => availableStepNames.includes(stepName);

const getInitialStandaloneStepName = ({
    createSteps,
    searchParams,
    isStandaloneStepEnabled,
}: {
    createSteps: () => CombinerStep[];
    searchParams: ReturnType<typeof urlParser>['params'];
    isStandaloneStepEnabled: boolean;
}) => {
    if (!isStandaloneStepEnabled) {
        return null;
    }

    const isPublicationTypeStepOpen = 'selectPublicationType' in searchParams;

    if (isPublicationTypeStepOpen) {
        return StepName.PublicationType;
    }

    const stepName = searchParams.stepName ? String(searchParams.stepName) : null;

    if (isStepName(stepName) && createSteps().find((step) => step.name === stepName && step.isStandalonable)) {
        return stepName;
    }

    return null;
};

const getInitialSteps = ({
    createSteps,
    standaloneStepName,
    isStandaloneStepEnabled,
}: {
    createSteps: () => CombinerStep[];
    standaloneStepName: StepName | null;
    isStandaloneStepEnabled: boolean;
}): Step[] =>
    createSteps().map((step) => {
        const isStandalonable = isStandaloneStepEnabled ? step.isStandalonable : false;
        const isHiddenInForm = isStandaloneStepEnabled && standaloneStepName ? step.name !== standaloneStepName : false;
        const sections = step.name === standaloneStepName ? step.standaloneSections : step.sections;

        return {
            name: step.name,
            sections,
            isStandalonable,
            isHiddenInForm,
            isHiddenInSidebar: step.isHiddenInSidebar,
        };
    });

const getInitialCurrentStepIndex = ({
    steps,
    standaloneStepName,
    showAllNotStandaloneSteps,
}: {
    steps: Step[];
    standaloneStepName: StepName | null;
    showAllNotStandaloneSteps?: boolean;
}) => {
    const standaloneStepIndex = standaloneStepName
        ? steps.findLastIndex((step) => step.name === standaloneStepName)
        : -1;
    const lastCoupledStepIndex = showAllNotStandaloneSteps ? steps.findLastIndex((step) => !step.isStandalonable) : -1;

    let currentStepIndex = 0;

    if (standaloneStepIndex >= 0) {
        currentStepIndex = standaloneStepIndex;
    } else if (lastCoupledStepIndex >= 0) {
        currentStepIndex = lastCoupledStepIndex;
    }

    return currentStepIndex;
};

const Features = {
    enableStandaloneStep: 'enable_vc_standalone_steps',
};

const useSteps = ({
    showAllNotStandaloneSteps,
    createSteps,
}: {
    showAllNotStandaloneSteps?: boolean;
    createSteps: () => CombinerStep[];
}) => {
    const { search } = useSelector((state) => state.router.location);
    const { params } = urlParser(search);

    const vacancyCreatePublicationSessionKey = useSelector((state) => state.vacancyCreatePublicationSessionKey);
    const prevVacancyCreatePublicationSessionKey = useRef(vacancyCreatePublicationSessionKey);

    const isBackoffice = useIsBackoffice();
    const isStandaloneStepEnabledByFeature = useFeatureEnabled(Features.enableStandaloneStep);
    const [isStandaloneStepEnabled, setStandaloneStepEnabled] = useState(
        !isBackoffice && isStandaloneStepEnabledByFeature
    );
    const prevIsStandaloneStepEnabled = useRef(isStandaloneStepEnabled);

    const [standaloneStepName, setStandaloneStepName] = useState(
        getInitialStandaloneStepName({
            createSteps,
            searchParams: params,
            isStandaloneStepEnabled,
        })
    );

    const [steps, setSteps] = useState(
        getInitialSteps({
            createSteps,
            standaloneStepName,
            isStandaloneStepEnabled,
        })
    );

    const [currentStepIndex, setCurrentStepIndex] = useState(
        getInitialCurrentStepIndex({ steps, standaloneStepName, showAllNotStandaloneSteps })
    );
    const [activeStepIndex, setActiveStepIndex] = useState(currentStepIndex);

    const recreateSteps = useCallback(
        (standaloneStepName: StepName | null) => {
            setStandaloneStepName(standaloneStepName);

            const steps = getInitialSteps({
                createSteps,
                standaloneStepName,
                isStandaloneStepEnabled,
            });
            setSteps(steps);
            setCurrentStepIndex(getInitialCurrentStepIndex({ steps, standaloneStepName, showAllNotStandaloneSteps }));
        },
        [createSteps, isStandaloneStepEnabled, showAllNotStandaloneSteps]
    );

    // Сбрасываем шаги и текущий шаг при:
    // 1. Изменении сессии/истории
    // 2. Изменении поддержки одиночных шагов
    useEffect(() => {
        if (
            vacancyCreatePublicationSessionKey !== prevVacancyCreatePublicationSessionKey.current ||
            isStandaloneStepEnabled !== prevIsStandaloneStepEnabled.current
        ) {
            recreateSteps(
                getInitialStandaloneStepName({
                    createSteps,
                    searchParams: params,
                    isStandaloneStepEnabled,
                })
            );
        }

        prevVacancyCreatePublicationSessionKey.current = vacancyCreatePublicationSessionKey;
        prevIsStandaloneStepEnabled.current = isStandaloneStepEnabled;
    }, [createSteps, isStandaloneStepEnabled, params, recreateSteps, vacancyCreatePublicationSessionKey]);

    const forceShowAllSteps = useCallback(() => {
        setCurrentStepIndex(steps.length - 1);
        setStandaloneStepEnabled(false);
    }, [setCurrentStepIndex, setStandaloneStepEnabled, steps.length]);

    return {
        steps,
        isStandaloneStepEnabled,
        standaloneStepName,
        currentStepIndex,
        activeStepIndex,
        forceShowAllSteps,
        setCurrentStepIndex,
        setActiveStepIndex,
        recreateSteps,
    };
};

export { useSteps };
